import { z } from 'zod'

/**
 * Creates a sequence generator function that produces formatted strings with a prefix and zero-padded numbers.
 *
 * This is a curried function that first takes configuration parameters to set up the sequence format,
 * then returns a function that can generate individual sequence strings within the specified range.
 *
 * @template Min - The minimum value type for the sequence
 * @template Max - The maximum value type for the sequence
 *
 * @param prefix - The string prefix to prepend to each generated sequence
 * @param min - The minimum value for the sequence
 * @param max - The maximum value for the sequence (determines padding length)
 *
 * @returns A function that takes an optional value and returns a formatted sequence string
 *
 * @example
 * ```typescript
 * // Create a generator for invoice numbers from 1-999
 * const invoiceGenerator = sequenceGenerator('INV-', 1, 999)
 *
 * // Generate specific invoice number
 * invoiceGenerator(42) // Returns: "INV-042"
 *
 * // Generate random invoice number
 * invoiceGenerator() // Returns: "INV-123" (random between 1-999)
 *
 * // Create account number generator with custom range
 * const accountGenerator = sequenceGenerator('ACC-', 1000, 9999)
 * accountGenerator(1234) // Returns: "ACC-1234"
 * ```
 */
export const sequenceGenerator = <
  Prefix extends string,
  Min extends number,
  Max extends number,
>(
  prefix: Prefix,
  min: Min,
  max: Max
) => {
  const minValue = z.int().min(1).parse(min)

  const maxValue = z.int().min(minValue).parse(max)

  const sequenceSchema = z
    .int()
    .min(minValue)
    .max(maxValue)
    .transform<`${Prefix}${string}`>(
      (input) =>
        `${prefix}${input.toString().padStart(maxValue.toString().length, '0')}`
    )

  return (
    seq: number = Math.floor(Math.random() * (maxValue - minValue + 1)) +
      minValue
  ) => sequenceSchema.parse(seq)
}
